// src/Pages/ValmikiFounder360.jsx
// FOUNDER 360 page for VALMIKI AI LAB (route: /founder-360).
// Self-contained: this file carries its own CSS in the `css` string below.
// Reuses the shared navbar's contact popup and route map.
// Content taken from Valmiki_Founder360_Bootcamp_Final.pdf.
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ValmikiContactModal, VALMIKI_ROUTES } from "./ValmikiNavbar";
import ValmikiFooter from "./ValmikiFooter"; // ← change if your footer lives elsewhere
import ValmikiLeadForm from "./ValmikiLeadPopup";

/* ───────────────────────── CONTENT ───────────────────────── */

const KIND_LABEL = {
  orientation: "Orientation",
  strategy: "Strategy",
  branding: "Branding",
  marketing: "Marketing",
  sales: "Sales",
  finance: "Finance",
  people: "People",
  operations: "Operations",
  analytics: "Analytics",
  ai: "AI",
  automation: "Automation",
  implementation: "Implementation",
  finale: "Grand Finale",
};

const KIND_CODE = {
  orientation: "OR",
  strategy: "ST",
  branding: "BR",
  marketing: "MK",
  sales: "SL",
  finance: "FI",
  people: "PE",
  operations: "OP",
  analytics: "AN",
  ai: "AI",
  automation: "AU",
  implementation: "IM",
  finale: "GF",
};

/* One short summary line per module. */
const SUMMARY_BY_KIND = {
  orientation:
    "Welcome, program orientation, founder mindset, and setting personal goals for the 21 days.",
  strategy:
    "Vision, business model, competitive strategy, and growth plans with real case studies.",
  branding:
    "Brand identity, positioning, storytelling, and building a consistent personal brand.",
  marketing:
    "Consumer behaviour, content, social media, and performance ads that reach customers.",
  sales:
    "Sales process, outreach, negotiation, closing, and building a working CRM pipeline.",
  finance:
    "Accounts, cash flow, pricing, funding, and reading financial statements with confidence.",
  people:
    "Hiring, freelancers, culture, performance, and the basics of labour law for founders.",
  operations:
    "SOPs, process design, supply chain, quality, and running the business day to day.",
  analytics:
    "Data-driven decisions, KPIs, dashboards, and reading market data to guide growth.",
  ai:
    "AI fundamentals, prompting, research, decisions, and building a custom AI assistant.",
  automation:
    "CRM, WhatsApp, Zapier / Make workflows, and setting up an AI agent team for the business.",
  implementation:
    "90-day action plan with targets, mentor one-on-ones, and your 360° business plan.",
  finale:
    "Grand Finale case competition, final presentations, certificates, and graduation.",
};

/* Grouped blocks shown in the "21-day plan" list. */
const BLOCKS = [
  { kind: "orientation", days: "Day 1" },
  { kind: "strategy", days: "Days 2, 3, 4, 5" },
  { kind: "marketing", days: "Days 6, 7, 8" },
  { kind: "sales", days: "Day 9" },
  { kind: "finance", days: "Days 10, 11" },
  { kind: "people", days: "Day 12" },
  { kind: "operations", days: "Days 13, 14" },
  { kind: "analytics", days: "Day 15" },
  { kind: "ai", days: "Days 16, 17" },
  { kind: "automation", days: "Days 18, 19" },
  { kind: "implementation", days: "Day 20" },
  { kind: "finale", days: "Day 21" },
];

const LEGEND = [
  ["orientation", "Orientation"],
  ["strategy", "Strategy"],
  ["marketing", "Marketing"],
  ["sales", "Sales"],
  ["finance", "Finance"],
  ["people", "People"],
  ["operations", "Operations"],
  ["analytics", "Analytics"],
  ["ai", "AI"],
  ["automation", "Automation"],
  ["implementation", "Implementation"],
  ["finale", "Grand Finale"],
];

const WEEKS = [
  {
    range: "Days 1–7",
    title: "Mindset, Strategy & Marketing",
    short: "Mindset, strategy & marketing",
    intro:
      "Build a winning mindset, master strategy, and start the deep dive into marketing.",
  },
  {
    range: "Days 8–14",
    title: "Marketing, Sales, Finance & Operations",
    short: "Marketing, sales, finance & operations",
    intro:
      "Turn attention into revenue, then master money, people and processes.",
  },
  {
    range: "Days 15–21",
    title: "Analytics, AI & Automation",
    short: "Analytics, AI & automation",
    intro:
      "Use data, AI and automation to run every function yourself, then prove it in the Grand Finale.",
  },
];

/* Day → kind map (still used by the hero calendar squares). */
const DAYS = [
  { n: 1, kind: "orientation" },
  { n: 2, kind: "strategy" },
  { n: 3, kind: "strategy" },
  { n: 4, kind: "strategy" },
  { n: 5, kind: "strategy" },
  { n: 6, kind: "marketing" },
  { n: 7, kind: "marketing" },
  { n: 8, kind: "marketing" },
  { n: 9, kind: "sales" },
  { n: 10, kind: "finance" },
  { n: 11, kind: "finance" },
  { n: 12, kind: "people" },
  { n: 13, kind: "operations" },
  { n: 14, kind: "operations" },
  { n: 15, kind: "analytics" },
  { n: 16, kind: "ai" },
  { n: 17, kind: "ai" },
  { n: 18, kind: "automation" },
  { n: 19, kind: "automation" },
  { n: 20, kind: "implementation" },
  { n: 21, kind: "finale" },
];

const STATS = [
  ["21", "Days, 6 AM to 10:30 PM"],
  ["8+ hrs", "Learning every day"],
  ["25+", "Real business case studies"],
  ["3", "Case competitions"],
];

const FACTS = [
  ["Who it is for", "Founders, business owners, professionals and aspiring entrepreneurs who want to run a company single-handedly"],
  ["Format", "21 days, fully residential, 6 AM to 10:30 PM; stay, meals, classes, sports and events included"],
  ["Learning method", "Case method + AI labs + daily implementation on your own business; classes run 9:30 AM to 8:30 PM"],
  ["Discipline", "Army-style training: fixed schedule, minimal idle time, daily targets and daily review"],
];

const MODULES = [
  { k: "strategy", name: "Strategy", blurb: "Business models, competition, growth, execution" },
  { k: "marketing", name: "Marketing", blurb: "Consumer, STP, content, performance ads" },
  { k: "branding", name: "Branding & Sales", blurb: "Positioning, personal brand, selling, closing" },
  { k: "finance", name: "Finance", blurb: "Accounts, cash flow, pricing, funding" },
  { k: "people", name: "People & Leadership", blurb: "Hiring, freelancers, culture, performance" },
  { k: "operations", name: "Operations", blurb: "SOPs, process, supply chain, projects" },
  { k: "analytics", name: "Business Analytics", blurb: "Data-driven decisions, KPIs, dashboards" },
  { k: "ai", name: "AI", blurb: "Prompting, research, decisions, custom AI" },
  { k: "automation", name: "Automation", blurb: "CRM, WhatsApp, Zapier/Make, AI agents" },
  { k: "communication", name: "Communication", blurb: "Speaking, pitching, negotiation" },
  { k: "yoga", name: "Fitness & Discipline", blurb: "Yoga, drills, sports, mindset" },
];

const TAKEAWAYS = [
  { k: "strategy", t: "Strategy Roadmap", d: "Business model, 1-3 year plan and OKRs" },
  { k: "branding", t: "Brand Kit", d: "Logo concepts, tagline and brand voice" },
  { k: "marketing", t: "Marketing Plan", d: "Content calendar + a live test ad campaign" },
  { k: "sales", t: "Sales Playbook", d: "Scripts, email sequences, CRM pipeline" },
  { k: "finance", t: "Financial Model", d: "Cash-flow forecast and investor pitch deck" },
  { k: "people", t: "People Toolkit", d: "JDs, freelancer briefs, HR policies" },
  { k: "operations", t: "SOPs + KPI Dashboard", d: "Documented processes and live tracking" },
  { k: "automation", t: "AI Agent Team", d: "Custom AI assistant, bots and live workflows" },
  { k: "implementation", t: "90-Day Action Plan", d: "Mentor-reviewed, with clear targets" },
  { k: "orientation", t: "Fitness Routine + Certificate", d: "A daily routine for life + Founder 360°" },
];

/* ───────────────────────── STYLES ───────────────────────── */

const css = `@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');
.f3{
  --ink:#0A1729; --ink-2:#132238;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --c-orientation:#1F4287; --c-strategy:#4F7A21; --c-branding:#C2410C; --c-marketing:#BE185D;
  --c-sales:#C2185B; --c-finance:#15803D; --c-people:#7C3AED; --c-operations:#0369A1;
  --c-analytics:#0F766E; --c-ai:#9A6412; --c-automation:#4338CA; --c-implementation:#9F1239;
  --c-finale:#BE123C; --c-communication:#0F766E; --c-yoga:#BE123C; --c-rest:#9AA6BA;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased;
}
.f3, .f3 *{box-sizing:border-box;}
.f3 :where(h1,h2,h3,h4,p,ul,ol,dl,dd){margin:0;padding:0;}
.f3 :where(ul,ol){list-style:none;}
.f3 :focus-visible{outline:2px solid var(--gold-deep);outline-offset:3px;}
.f3 .f3-dark :focus-visible, .f3 .f3-dark:focus-visible{outline-color:var(--gold-hi);}
.f3-wrap{max-width:1280px;margin:0 auto;padding:0 32px;}
@media (max-width:560px){.f3-wrap{padding:0 20px;}}

.f3-btn{
  --fill:#162B47; --edge:var(--gold); --runline:var(--gold); --wipe:var(--gold);
  --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:15px 28px;border-radius:2px;
  font:600 14px/1 var(--sans);letter-spacing:.06em;text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.f3-btn::before,.f3-btn::after{content:"";position:absolute;left:0;width:100%;height:3px;pointer-events:none;}
.f3-btn::before{top:0;background:linear-gradient(90deg,transparent 70%,var(--runline) 70%);
  animation:f3-run-top 2.4s cubic-bezier(.65,0,.35,1) infinite;}
.f3-btn::after{bottom:0;background:linear-gradient(90deg,var(--runline) 30%,transparent 30%);
  animation:f3-run-bottom 2.4s cubic-bezier(.65,0,.35,1) infinite;}
@keyframes f3-run-top{0%{transform:translateX(-100%);}60%,100%{transform:translateX(100%);}}
@keyframes f3-run-bottom{0%{transform:translateX(100%);}60%,100%{transform:translateX(-100%);}}
.f3-btn:hover,.f3-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);--runline:var(--hover-text);}
.f3-btn--ghost{--fill:transparent;--edge:rgba(243,245,248,.45);--runline:rgba(243,245,248,.75);--wipe:var(--on-ink);}
.f3-actions{display:flex;flex-wrap:wrap;gap:14px;}

.f3-sec{padding:92px 0;}
.f3-sec--white{background:var(--white);}
.f3-sec--paper{background:var(--paper);}
.f3-h2{font:500 clamp(1.9rem,3.4vw,2.7rem)/1.15 var(--serif);letter-spacing:-.01em;color:var(--ink);margin-bottom:14px;}
.f3-lede{font-size:1.05rem;line-height:1.65;color:var(--slate);max-width:44rem;}
.f3 [id^="f3-"]{scroll-margin-top:110px;}

.f3-hero{background:var(--ink);color:var(--on-ink);border-bottom:3px solid var(--gold);}
.f3-hero-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.05fr);gap:64px;align-items:center;padding:84px 0 68px;}
.f3-kicker{font:500 15px/1.4 var(--sans);color:var(--gold-hi);margin-bottom:20px;}
.f3-title{font:500 clamp(3.6rem,8.4vw,7rem)/.95 var(--serif);letter-spacing:-.03em;color:var(--white);margin-bottom:26px;}
.f3-title span{display:block;}
.f3-sub{font:400 clamp(1.1rem,1.6vw,1.35rem)/1.5 var(--serif);color:var(--on-ink-mute);max-width:30em;margin-bottom:34px;}

.f3-cal{display:grid;gap:20px;}
.f3-cal-cap{font:400 13px/1.3 var(--sans);color:var(--on-ink-mute);margin-bottom:8px;}
.f3-cal-cap b{color:var(--on-ink);font-weight:600;margin-right:8px;}
.f3-cal-row{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px;}
.f3-cell{
  aspect-ratio:1/1;border:0;border-radius:3px;background:var(--k);color:#fff;
  font:600 clamp(.85rem,1.5vw,1.15rem)/1 var(--sans);letter-spacing:.1em;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  animation:f3-pop .32s cubic-bezier(.2,.7,.2,1) backwards;animation-delay:calc(var(--i) * 26ms);
  transition:transform .15s ease,box-shadow .15s ease;
}
.f3-cell:hover,.f3-cell:focus-visible{transform:translateY(-3px);box-shadow:0 8px 16px rgba(0,0,0,.4);}
@keyframes f3-pop{from{opacity:0;transform:scale(.55);}to{opacity:1;transform:none;}}
.f3-peek{min-height:3.4em;margin-top:18px;font-size:14px;line-height:1.55;color:var(--on-ink);}
.f3-legend{display:flex;flex-wrap:wrap;gap:8px 16px;margin-top:6px;}
.f3-legend li{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;color:var(--on-ink-mute);}
.f3-legend i{display:inline-block;width:10px;height:10px;border-radius:2px;background:var(--k);}

.f3-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));border-top:1px solid var(--line-ink);}
.f3-stat{padding:26px 24px 30px 0;}
.f3-stat + .f3-stat{border-left:1px solid var(--line-ink);padding-left:24px;}
.f3-stat-v{font:500 2.3rem/1.1 var(--serif);color:var(--gold-hi);margin-bottom:4px;}
.f3-stat-l{font-size:14px;color:var(--on-ink-mute);}

.f3-two{display:grid;grid-template-columns:minmax(0,5fr) minmax(0,7fr);gap:72px;align-items:start;}
.f3-facts > div{display:grid;grid-template-columns:150px minmax(0,1fr);gap:20px;padding:18px 0;border-top:1px solid var(--line);}
.f3-facts > div:last-child{border-bottom:1px solid var(--line);}
.f3-facts dt{font-weight:600;color:var(--ink);}
.f3-facts dd{color:var(--slate);}
.f3-diff{margin-top:60px;padding:6px 0 6px 28px;border-left:3px solid var(--gold);max-width:58rem;}
.f3-diff h3{font:500 clamp(1.5rem,2.4vw,1.9rem)/1.25 var(--serif);margin-bottom:10px;}
.f3-diff p{font-size:1.05rem;line-height:1.7;color:var(--slate);}

.f3-mods{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-top:40px;}
.f3-mod{background:var(--k);color:#fff;padding:24px 26px 26px;border-radius:3px;min-height:132px;}
.f3-mod h3{font:500 1.45rem/1.2 var(--serif);margin-bottom:8px;}
.f3-mod p{font-size:.95rem;line-height:1.5;color:rgba(255,255,255,.92);}

/* ── day by day (grouped blocks) ── */
.f3-block-list{display:grid;gap:0;margin-top:32px;}
.f3-block-item{
  display:grid;grid-template-columns:72px minmax(0,1fr);column-gap:22px;
  align-items:start;
  padding:22px 0;
  border-bottom:1px solid var(--line);
}
.f3-block-item:last-child{border-bottom:0;}
.f3-block-code{
  background:var(--k);color:#fff;
  width:64px;height:64px;border-radius:4px;
  display:flex;align-items:center;justify-content:center;
  font:700 .95rem/1 var(--sans);letter-spacing:.14em;
  margin-top:2px;
}
.f3-block-body{min-width:0;}
.f3-block-days{
  font:500 13px/1.4 var(--sans);color:var(--slate);letter-spacing:.04em;
  text-transform:uppercase;margin-bottom:4px;
}
.f3-block-heading{
  font:500 clamp(1.3rem,2.2vw,1.6rem)/1.2 var(--serif);
  color:var(--k);margin-bottom:6px;
}
.f3-block-desc{font-size:15px;line-height:1.6;color:var(--slate);}

.f3-out{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px 64px;margin-top:40px;}
.f3-out li{border-left:4px solid var(--k);padding:10px 0 10px 20px;}
.f3-out h3{font:500 1.3rem/1.25 var(--serif);margin-bottom:2px;}
.f3-out p{color:var(--slate);font-size:15px;}

/* ── closing CTA — gold background (#E0BC62) ── */
.f3-cta{background:#E0BC62;color:var(--ink);border-top:3px solid var(--gold-deep);padding:92px 0;}
.f3-cta-grid{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(0,1fr);gap:64px;align-items:end;}
.f3-cta h2{font:500 clamp(2.2rem,4.6vw,3.8rem)/1.08 var(--serif);letter-spacing:-.02em;color:var(--ink);}
.f3-cta h2 span{display:block;}
.f3-cta p{font-size:1.1rem;line-height:1.65;color:rgba(10,23,41,.78);margin-bottom:28px;max-width:30em;}

/* Buttons inside the gold CTA use the deep navy so they stand out */
.f3-cta .f3-btn{
  --fill:#0A1729; --edge:#0A1729; --runline:var(--gold-hi); --wipe:var(--gold-hi);
  --text:var(--on-ink); --hover-text:var(--ink);
}
.f3-cta .f3-btn--ghost{
  --fill:transparent; --edge:rgba(10,23,41,.55); --runline:rgba(10,23,41,.85);
  --wipe:#0A1729; --text:var(--ink); --hover-text:var(--on-ink);
}

@media (max-width:980px){
  .f3-hero-grid{grid-template-columns:minmax(0,1fr);gap:48px;padding:60px 0 52px;}
  .f3-two{grid-template-columns:minmax(0,1fr);gap:32px;}
  .f3-mods{grid-template-columns:repeat(2,minmax(0,1fr));}
  .f3-cta-grid{grid-template-columns:minmax(0,1fr);gap:32px;align-items:start;}
}
@media (max-width:720px){
  .f3-sec{padding:64px 0;}
  .f3-cta{padding:64px 0;}
  .f3-stats{grid-template-columns:repeat(2,minmax(0,1fr));}
  .f3-stat:nth-child(odd){border-left:0;padding-left:0;}
  .f3-stat:nth-child(even){border-left:1px solid var(--line-ink);padding-left:24px;}
  .f3-stat:nth-child(n+3){border-top:1px solid var(--line-ink);}
  .f3-facts > div{grid-template-columns:minmax(0,1fr);gap:4px;}
  .f3-mods{grid-template-columns:minmax(0,1fr);}
  .f3-out{grid-template-columns:minmax(0,1fr);}
  .f3-block-item{grid-template-columns:56px minmax(0,1fr);column-gap:16px;padding:18px 0;}
  .f3-block-code{width:48px;height:48px;font-size:.8rem;}
  .f3-block-heading{font-size:1.2rem;}
  .f3-block-desc{font-size:14.5px;}
}
@media (max-width:420px){
  .f3-btn{width:100%;}
}
@media (prefers-reduced-motion:reduce){
  .f3-btn,.f3-cell,.f3-block-item{transition:none;}
  .f3-btn::before,.f3-btn::after{animation:none;}
  .f3-cell{animation:none;}
}
`;

/* ───────────────────────── PAGE ───────────────────────── */

const kindStyle = (kind, extra = {}) => ({ "--k": `var(--c-${kind})`, ...extra });

export default function ValmikiFounder360() {
  const [peek, setPeek] = useState(null);
  const [focusKind, setFocusKind] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  const closeContact = useCallback(() => setContactOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = "Founder 360° | VALMIKI AI LAB";
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    if (focusKind == null) return undefined;
    const t = setTimeout(() => setFocusKind(null), 2600);
    return () => clearTimeout(t);
  }, [focusKind]);

  const jumpToKind = (kind) => {
    setFocusKind(kind);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setTimeout(() => {
      const el = document.getElementById(`f3-block-${kind}`);
      if (el) el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
    }, 60);
  };

  return (
    <div className="f3">
      <style>{css}</style>

      {/* ───────── HERO ───────── */}
      <section className="f3-hero f3-dark" aria-labelledby="f3-title">
        <div className="f3-wrap">
          <div className="f3-hero-grid">
            <div>
              <p className="f3-kicker">21-day residential business bootcamp</p>
              <h1 className="f3-title" id="f3-title">
                <span>Founder</span>
                <span>360°</span>
              </h1>
              <p className="f3-sub">
                Case-based training in strategy, marketing, finance, AI and automation to run a
                complete company single-handedly.
              </p>
              <div className="f3-actions">
                <button type="button" className="f3-btn" onClick={() => setContactOpen(true)}>
                  RESERVE YOUR SEAT
                </button>
                <a className="f3-btn f3-btn--ghost" href="#f3-days">
                  SEE THE 21 DAYS
                </a>
              </div>
            </div>

            <div className="f3-cal">
              {WEEKS.map((w, wi) => (
                <div key={w.range}>
                  <p className="f3-cal-cap">
                    <b>Week {wi + 1}</b>
                    {w.short}
                  </p>
                  <div className="f3-cal-row">
                    {DAYS.slice(wi * 7, wi * 7 + 7).map((d) => (
                      <button
                        key={d.n}
                        type="button"
                        className="f3-cell"
                        style={kindStyle(d.kind, { "--i": d.n - 1 })}
                        onMouseEnter={() => setPeek(d)}
                        onMouseLeave={() => setPeek(null)}
                        onFocus={() => setPeek(d)}
                        onBlur={() => setPeek(null)}
                        onClick={() => jumpToKind(d.kind)}
                        aria-label={`Day ${d.n}. ${KIND_LABEL[d.kind]}. Jump to this block.`}
                      >
                        {KIND_CODE[d.kind]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <p className="f3-peek">
                  {peek
                    ? `Day ${peek.n} · ${KIND_LABEL[peek.kind]}`
                    : "Each square is one day. Select a square to jump to its module."}
                </p>
                <ul className="f3-legend" aria-label="Colour key">
                  {LEGEND.map(([k, name]) => (
                    <li key={k}>
                      <i style={kindStyle(k)} aria-hidden="true" />
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <ul className="f3-stats">
            {STATS.map(([v, l]) => (
              <li className="f3-stat" key={l}>
                <p className="f3-stat-v">{v}</p>
                <p className="f3-stat-l">{l}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───────── OVERVIEW ───────── */}
      <section className="f3-sec f3-sec--white" id="f3-overview" aria-labelledby="f3-overview-h">
        <div className="f3-wrap">
          <div className="f3-two">
            <div>
              <h2 className="f3-h2" id="f3-overview-h">Master every side of running a business</h2>
              <p className="f3-lede">
                An intensive 21-day residential bootcamp that builds a rock-solid business
                foundation. Participants learn the way top business schools teach, through real case
                studies, then implement everything with AI and automation, so they can run every
                function of a company on their own.
              </p>
            </div>
            <dl className="f3-facts">
              {FACTS.map(([t, d]) => (
                <div key={t}>
                  <dt>{t}</dt>
                  <dd>{d}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="f3-diff">
            <h3>B-school depth. Bootcamp discipline.</h3>
            <p>
              Every day runs on Learn, Analyse the Case, Apply with AI, Implement. Participants
              practise the way leading business schools train managers, but every concept is turned
              into a working plan, model or system for their own business the same day.
            </p>
          </div>
        </div>
      </section>

      {/* ───────── MODULES ───────── */}
      <section className="f3-sec f3-sec--paper" id="f3-modules" aria-labelledby="f3-modules-h">
        <div className="f3-wrap">
          <h2 className="f3-h2" id="f3-modules-h">11 modules</h2>
          <p className="f3-lede">
            Three weeks, eleven modules, three case competitions. Each tile is one full day.
          </p>
          <ul className="f3-mods">
            {MODULES.map((m) => (
              <li className="f3-mod" key={m.k} style={kindStyle(m.k)}>
                <h3>{m.name}</h3>
                <p>{m.blurb}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───────── DAY BY DAY (grouped blocks) ───────── */}
      <section className="f3-sec f3-sec--paper" id="f3-days" aria-labelledby="f3-days-h">
        <div className="f3-wrap">
          <h2 className="f3-h2" id="f3-days-h">The 21-day plan</h2>
          <p className="f3-lede">
            Three weeks, eleven modules, three case competitions. Each tile is one full day.
          </p>

          <ul className="f3-block-list">
            {BLOCKS.map((b) => (
              <li
                key={b.kind}
                id={`f3-block-${b.kind}`}
                className="f3-block-item"
                style={kindStyle(b.kind)}
              >
                <span className="f3-block-code" aria-hidden="true">
                  {KIND_CODE[b.kind]}
                </span>
                <div className="f3-block-body">
                  <p className="f3-block-days">{b.days}</p>
                  <h3 className="f3-block-heading">{KIND_LABEL[b.kind]}</h3>
                  <p className="f3-block-desc">{SUMMARY_BY_KIND[b.kind]}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───────── TAKE-HOME ───────── */}
      <section className="f3-sec f3-sec--white" id="f3-outcomes" aria-labelledby="f3-outcomes-h">
        <div className="f3-wrap">
          <h2 className="f3-h2" id="f3-outcomes-h">What participants take home</h2>
          <p className="f3-lede">
            Every participant leaves with working plans and live systems for their own business, and
            the confidence to run every function of a company alone.
          </p>
          <ul className="f3-out">
            {TAKEAWAYS.map((t) => (
              <li key={t.t} style={kindStyle(t.k)}>
                <h3>{t.t}</h3>
                <p>{t.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="f3-cta f3-dark" aria-labelledby="f3-cta-h">
        <div className="f3-wrap">
          <div className="f3-cta-grid">
            <h2 id="f3-cta-h">
              <span>Strong base.</span>
              <span>Real skills.</span>
              <span>Zero excuses.</span>
            </h2>
            <div>
              <p>Limited to 20–30 participants per batch. Contact Valmiki AI Lab to reserve your seat.</p>
              <div className="f3-actions">
                <button type="button" className="f3-btn" onClick={() => setContactOpen(true)}>
                  RESERVE YOUR SEAT
                </button>
                <Link className="f3-btn f3-btn--ghost" to={VALMIKI_ROUTES.contact}>
                  CONTACT PAGE
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValmikiContactModal open={contactOpen} onClose={closeContact} />
      <ValmikiFooter />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}